import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class GetPresignedDownloadURLForStatsRPC implements
  LeoRPC<GetPresignedDownloadURLForStatsRPC.Request, GetPresignedDownloadURLForStatsRPC.Response, GetPresignedDownloadURLForStatsRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedDownloadURLForStatsRPC.Request
  ): Promise<LeoRPCResult<GetPresignedDownloadURLForStatsRPC.Response, GetPresignedDownloadURLForStatsRPC.Errors.Errors>>;
}

export namespace GetPresignedDownloadURLForStatsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace StatType {
      export enum StatType {
        OPT_OUT = "OPT_OUT",
        UNSENT = "UNSENT",
        BOUNCES_AND_COMPLAINTS = "BOUNCES_AND_COMPLAINTS",
      }
    
      export interface StatTypeDTO {
        case: string;
      }
    
      export const fromDTO = (dto: StatTypeDTO): StatType => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestStatTypeError(
            `Invalid DTO ${dto} provided for StatType.StatType`
          );
        }
        switch (dto.case) {
          case "OPT_OUT":
            return StatType.OPT_OUT;
          case "UNSENT":
            return StatType.UNSENT;
          case "BOUNCES_AND_COMPLAINTS":
            return StatType.BOUNCES_AND_COMPLAINTS;
          default:
            throw new InvalidRequestStatTypeError(
              `Case ${dto.case} is not valid case of StatType.StatType`
            );
        }
      };
    
      export const toDTO = (statType: StatType): StatTypeDTO => {
        const enumDTO = {
          case: StatType[statType],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    projectId: string;
    statType: RequestEnums.StatType.StatTypeDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedDownloadURLForStatsRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly statType: RequestEnums.StatType.StatType;
  
  
    constructor(
    projectId: LeoUUID, 
    statType: RequestEnums.StatType.StatType
    ) {
      this.projectId = projectId;
      this.statType = statType;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const statType = RequestEnums.StatType.fromDTO(getObject(dto, "statType", InvalidRequestStatTypeError));
      return new Request(
        projectId,
        statType
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          statType: RequestEnums.StatType.toDTO(this.statType)
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForStatsRPC(projectId='${this.projectId}',statType='${this.statType}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedDownloadURLForStatsResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForStats(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLForStatsRPC.INVALID_PROJECT_ID()`;
    };
    

    export type Errors = InvalidProjectId;
    export type ErrorsDTO = InvalidProjectIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetPresignedDownloadURLForStatsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestStatTypeError extends GetPresignedDownloadURLForStatsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestStatTypeError";
  }
}

export class InvalidRequestStatTypeOptOutError extends InvalidRequestStatTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestStatTypeOptOutError";
  }
}

export class InvalidRequestStatTypeUnsentError extends InvalidRequestStatTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestStatTypeUnsentError";
  }
}

export class InvalidRequestStatTypeBouncesAndComplaintsError extends InvalidRequestStatTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestStatTypeBouncesAndComplaintsError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedDownloadURLForStatsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPresignedDownloadURLForStatsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

